import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { 
  FileText, 
  Download,
  Calendar,
  Users,
  Bed,
  DollarSign,
  Percent,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Clock,
  RefreshCw,
  Loader2,
  Filter,
  Play,
  ChevronRight,
  Building2,
  CheckCircle2,
  AlertTriangle,
  Home,
  CalendarDays,
  UserCheck,
  UserMinus,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { toast } from 'sonner';

export default function ReportsPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // KPI Dashboard data
  const [kpiData, setKpiData] = useState(null);
  const [kpiTrends, setKpiTrends] = useState([]);
  
  // Report data
  const [reportDefinitions, setReportDefinitions] = useState([]);
  const [reportRuns, setReportRuns] = useState([]);
  const [exports, setExports] = useState([]);
  
  // Quick report data
  const [quickReport, setQuickReport] = useState(null);
  const [quickReportType, setQuickReportType] = useState(null);
  
  // Date range
  const [reportDate, setReportDate] = useState(new Date());
  
  // Loading states
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loadingQuickReport, setLoadingQuickReport] = useState(false);

  useEffect(() => {
    if (currentProperty?.id) {
      loadKpiDashboard();
      loadReportDefinitions();
      loadReportRuns();
    }
  }, [currentProperty]);

  const loadKpiDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/reports/kpi/dashboard?property_id=${currentProperty.id}`);
      setKpiData(response.data);
      
      // Load trends
      const trendsResponse = await api.get(`/reports/kpi/trends?property_id=${currentProperty.id}&days=7`);
      setKpiTrends(trendsResponse.data.trends || []);
    } catch (error) {
      console.error('Error loading KPI dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportDefinitions = async () => {
    try {
      const response = await api.get(`/reports/definitions?property_id=${currentProperty.id}`);
      setReportDefinitions(response.data || []);
    } catch (error) {
      console.error('Error loading report definitions:', error);
    }
  };

  const loadReportRuns = async () => {
    try {
      const response = await api.get(`/reports/runs?property_id=${currentProperty.id}&limit=20`);
      setReportRuns(response.data || []);
    } catch (error) {
      console.error('Error loading report runs:', error);
    }
  };

  const loadExports = async () => {
    try {
      const response = await api.get(`/reports/exports?property_id=${currentProperty.id}`);
      setExports(response.data || []);
    } catch (error) {
      console.error('Error loading exports:', error);
    }
  };

  const loadQuickReport = async (reportType) => {
    try {
      setLoadingQuickReport(true);
      setQuickReportType(reportType);
      const dateStr = format(reportDate, 'yyyy-MM-dd');
      const response = await api.get(`/reports/quick/${reportType}?property_id=${currentProperty.id}&report_date=${dateStr}`);
      setQuickReport(response.data);
    } catch (error) {
      console.error('Error loading quick report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoadingQuickReport(false);
    }
  };

  const handleRunReport = async (reportId) => {
    try {
      setGeneratingReport(true);
      const response = await api.post(`/reports/run/${reportId}`, {
        report_date: format(reportDate, 'yyyy-MM-dd'),
        output_format: 'PDF'
      });
      toast.success('Report generated successfully');
      loadReportRuns();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleExportData = async (exportType, fmt = 'csv') => {
    try {
      setGeneratingReport(true);
      const response = await api.get('/reports/data-export', {
        params: { property_id: currentProperty.id, export_type: exportType, format: fmt },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportType.toLowerCase()}_${format(new Date(), 'yyyy-MM-dd')}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Map quick-report ids to export report_type values
  const exportTypeMap = {
    arrivals: 'ARRIVALS',
    departures: 'DEPARTURES',
    inhouse: 'IN_HOUSE',
    occupancy: 'OCCUPANCY',
    'room-status': 'ROOM_STATUS',
  };

  const handleDownloadReport = async (fmt) => {
    if (!quickReportType) return;
    const reportType = exportTypeMap[quickReportType];
    if (!reportType) {
      toast.error('This report cannot be exported');
      return;
    }
    try {
      const dateStr = format(reportDate, 'yyyy-MM-dd');
      const response = await api.get('/reports/export', {
        params: { property_id: currentProperty.id, report_type: reportType, format: fmt, report_date: dateStr },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType.toLowerCase()}_${dateStr}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property to view reports</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const quickReports = [
    { id: 'arrivals', name: 'Arrivals', icon: UserCheck, color: 'bg-blue-100 dark:bg-blue-900', iconColor: 'text-blue-600 dark:text-blue-400' },
    { id: 'departures', name: 'Departures', icon: UserMinus, color: 'bg-orange-100 dark:bg-orange-900', iconColor: 'text-orange-600 dark:text-orange-400' },
    { id: 'inhouse', name: 'In-House Guests', icon: Users, color: 'bg-green-100 dark:bg-green-900', iconColor: 'text-green-600 dark:text-green-400' },
    { id: 'occupancy', name: 'Occupancy', icon: Percent, color: 'bg-purple-100 dark:bg-purple-900', iconColor: 'text-purple-600 dark:text-purple-400' },
    { id: 'room-status', name: 'Room Status', icon: Bed, color: 'bg-cyan-100 dark:bg-cyan-900', iconColor: 'text-cyan-600 dark:text-cyan-400' },
  ];

  const exportTypes = [
    { type: 'RESERVATIONS', name: 'Reservations', description: 'Export all reservations data' },
    { type: 'GUESTS', name: 'Guest Profiles', description: 'Export guest information' },
    { type: 'TRANSACTIONS', name: 'Transactions', description: 'Export folio transactions' },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports & BI</h1>
          <p className="text-muted-foreground">Operational reports, KPI dashboards, and data exports</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="report-date-picker">
                <CalendarDays className="mr-2 h-4 w-4" />
                {format(reportDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={reportDate}
                onSelect={(date) => date && setReportDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button 
            variant="outline" 
            onClick={loadKpiDashboard}
            data-testid="refresh-reports-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">KPI Dashboard</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quick">Quick Reports</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled Reports</TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports">Data Exports</TabsTrigger>
        </TabsList>

        {/* KPI Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Today's KPIs */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="kpi-occupancy">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Occupancy</CardTitle>
                <Percent className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(kpiData?.kpis?.occupancy?.value)}</div>
                <p className="text-xs text-muted-foreground">
                  {kpiData?.kpis?.occupancy?.rooms_occupied || 0} of {kpiData?.kpis?.occupancy?.total_rooms || 0} rooms
                </p>
                <Progress 
                  value={kpiData?.kpis?.occupancy?.value || 0} 
                  className="h-2 mt-2"
                />
              </CardContent>
            </Card>
            
            <Card data-testid="kpi-adr">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">ADR</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(kpiData?.kpis?.adr?.value)}</div>
                <p className="text-xs text-muted-foreground">Average Daily Rate</p>
              </CardContent>
            </Card>
            
            <Card data-testid="kpi-revpar">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">RevPAR</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(kpiData?.kpis?.revpar?.value)}</div>
                <p className="text-xs text-muted-foreground">Revenue per Available Room</p>
              </CardContent>
            </Card>
            
            <Card data-testid="kpi-revenue">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today&apos;s Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(kpiData?.kpis?.revenue_today?.value)}</div>
                <p className="text-xs text-muted-foreground">Total revenue today</p>
              </CardContent>
            </Card>
          </div>

          {/* Activity Summary */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Arrivals Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{kpiData?.activity?.arrivals_today || 0}</div>
                    <p className="text-xs text-muted-foreground">Expected check-ins</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Departures Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                    <UserMinus className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{kpiData?.activity?.departures_today || 0}</div>
                    <p className="text-xs text-muted-foreground">Expected check-outs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">In-House Guests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{kpiData?.activity?.in_house || 0}</div>
                    <p className="text-xs text-muted-foreground">Currently staying</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 7-Day Trends */}
          {kpiTrends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>7-Day Performance Trend</CardTitle>
                <CardDescription>Daily metrics over the past week</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Occupancy</TableHead>
                      <TableHead className="text-right">ADR</TableHead>
                      <TableHead className="text-right">RevPAR</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kpiTrends.map((day) => (
                      <TableRow key={day.stat_date}>
                        <TableCell>{format(new Date(day.stat_date), 'EEE, MMM d')}</TableCell>
                        <TableCell className="text-right">{formatPercent(day.occupancy_percent)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.adr)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.revpar)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.total_revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Quick Reports Tab */}
        <TabsContent value="quick" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quickReports.map((report) => {
              const Icon = report.icon;
              return (
                <Card 
                  key={report.id} 
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => loadQuickReport(report.id)}
                  data-testid={`quick-report-${report.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`h-12 w-12 rounded-lg ${report.color} flex items-center justify-center`}>
                          <Icon className={`h-6 w-6 ${report.iconColor}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{report.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {format(reportDate, 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quick Report Results */}
          {loadingQuickReport && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </CardContent>
            </Card>
          )}

          {quickReport && !loadingQuickReport && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{quickReport.title}</CardTitle>
                    <CardDescription>
                      Generated on {format(new Date(), 'MMM d, yyyy h:mm a')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleDownloadReport('pdf')} data-testid="export-pdf">
                      <Download className="mr-2 h-4 w-4" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadReport('xlsx')} data-testid="export-xlsx">
                      <Download className="mr-2 h-4 w-4" />
                      Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadReport('csv')} data-testid="export-csv">
                      <Download className="mr-2 h-4 w-4" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                {quickReport.summary && (
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                    {Object.entries(quickReport.summary).map(([key, value]) => (
                      <div key={key} className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-2xl font-bold">
                          {typeof value === 'number' 
                            ? key.includes('percent') 
                              ? formatPercent(value)
                              : key.includes('revenue') || key.includes('amount')
                                ? formatCurrency(value)
                                : value
                            : value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Data Table */}
                {quickReport.rows && quickReport.rows.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {quickReportType === 'arrivals' || quickReportType === 'departures' ? (
                          <>
                            <TableHead>Confirmation #</TableHead>
                            <TableHead>Guest Name</TableHead>
                            <TableHead>Room</TableHead>
                            <TableHead>Status</TableHead>
                          </>
                        ) : quickReportType === 'inhouse' ? (
                          <>
                            <TableHead>Guest Name</TableHead>
                            <TableHead>Room</TableHead>
                            <TableHead>Arrival</TableHead>
                            <TableHead>Departure</TableHead>
                          </>
                        ) : quickReportType === 'room-status' ? (
                          <>
                            <TableHead>Room</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Floor</TableHead>
                          </>
                        ) : (
                          <TableHead>Data</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quickReport.rows.slice(0, 20).map((row, idx) => (
                        <TableRow key={row.id || idx}>
                          {quickReportType === 'arrivals' || quickReportType === 'departures' ? (
                            <>
                              <TableCell>{row.confirmation_number}</TableCell>
                              <TableCell>{row.guest_name || `${row.guests?.first_name} ${row.guests?.last_name}`}</TableCell>
                              <TableCell>{row.rooms?.room_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant={row.status === 'CHECKED_IN' ? 'default' : 'secondary'}>
                                  {row.status}
                                </Badge>
                              </TableCell>
                            </>
                          ) : quickReportType === 'inhouse' ? (
                            <>
                              <TableCell>{row.guest_name || `${row.guests?.first_name} ${row.guests?.last_name}`}</TableCell>
                              <TableCell>{row.rooms?.room_number || '-'}</TableCell>
                              <TableCell>{row.arrival_date}</TableCell>
                              <TableCell>{row.departure_date}</TableCell>
                            </>
                          ) : quickReportType === 'room-status' ? (
                            <>
                              <TableCell>{row.room_number}</TableCell>
                              <TableCell>{row.room_types?.name || '-'}</TableCell>
                              <TableCell>
                                <Badge>{row.maintenance_status || 'Unknown'}</Badge>
                              </TableCell>
                              <TableCell>{row.floor}</TableCell>
                            </>
                          ) : (
                            <TableCell>{JSON.stringify(row)}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {quickReport.rows && quickReport.rows.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No data available for this report</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Report Definitions</CardTitle>
                  <CardDescription>Configure and manage scheduled reports</CardDescription>
                </div>
                <Button data-testid="create-report-btn">
                  <FileText className="mr-2 h-4 w-4" />
                  Create Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {reportDefinitions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportDefinitions.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.report_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{report.report_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {report.schedule_enabled 
                            ? `${report.schedule_frequency} at ${report.schedule_time}`
                            : 'On demand'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={report.is_active ? 'default' : 'secondary'}>
                            {report.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRunReport(report.id)}
                            disabled={generatingReport}
                          >
                            {generatingReport ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No report definitions configured</p>
                  <Button variant="link" className="mt-2">Create your first report</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Report History */}
          <Card>
            <CardHeader>
              <CardTitle>Report History</CardTitle>
              <CardDescription>Recent report executions</CardDescription>
            </CardHeader>
            <CardContent>
              {reportRuns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report</TableHead>
                      <TableHead>Run Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Rows</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportRuns.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell className="font-medium">
                          {run.report_definitions?.report_name || 'Unknown'}
                        </TableCell>
                        <TableCell>{format(new Date(run.run_time), 'MMM d, h:mm a')}</TableCell>
                        <TableCell>
                          <Badge variant={run.status === 'COMPLETED' ? 'default' : 
                                        run.status === 'FAILED' ? 'destructive' : 'secondary'}>
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{run.row_count || '-'}</TableCell>
                        <TableCell className="text-right">
                          {run.status === 'COMPLETED' && (
                            <Button variant="ghost" size="sm">
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No report history available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Exports Tab */}
        <TabsContent value="exports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Export Data</CardTitle>
              <CardDescription>Download your data in various formats</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {exportTypes.map((exp) => (
                  <Card 
                    key={exp.type}
                    className="cursor-pointer hover:border-primary transition-colors"
                    data-testid={`export-${exp.type.toLowerCase()}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{exp.name}</h3>
                          <p className="text-sm text-muted-foreground">{exp.description}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleExportData(exp.type)}
                          disabled={generatingReport}
                        >
                          {generatingReport ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Export History */}
          <Card>
            <CardHeader>
              <CardTitle>Export History</CardTitle>
              <CardDescription>Previous data exports (available for 7 days)</CardDescription>
            </CardHeader>
            <CardContent>
              {exports.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Export Type</TableHead>
                      <TableHead>Date Range</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exports.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell className="font-medium">{exp.export_type}</TableCell>
                        <TableCell>
                          {exp.date_from && exp.date_to 
                            ? `${exp.date_from} - ${exp.date_to}`
                            : 'All data'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={exp.status === 'COMPLETED' ? 'default' : 
                                        exp.status === 'FAILED' ? 'destructive' : 'secondary'}>
                            {exp.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{exp.output_format}</TableCell>
                        <TableCell className="text-right">
                          {exp.status === 'COMPLETED' && (
                            <Button variant="ghost" size="sm" onClick={() => handleExportData(exp.export_type, (exp.output_format || 'CSV').toLowerCase())}>
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Download className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No export history available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
